import { CheckCircle2, ChevronDown, Menu, Phone, X, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { formatPhone, phoneHref } from "@servicepro/utils";
import { BOLD_CONTAINER_CLASS } from "../ui/bold-container";
import type { BaseComponentProps } from '../../types/puck';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import React from "react";
import { SmartLink } from "../smart-link";
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";
import { getBusinessInitials } from "../../lib/business-initials";
import { getWindowJobsiteConfig } from "../../types/runtime-config";
import { submitLead } from "../../forms/submit-lead";

interface NavigationLink {
  label: string;
  linkType: "page" | "url" | "anchor" | "dropdown";
  pageId?: string;
  url?: string;
  children?: NavigationLink[];
}

interface NavigationProps extends BaseComponentProps {
  logo?: string;
  /**
   * Logo used while a transparent header sits over the hero photo. A one-colour mark that
   * reads on a light page usually disappears against photography, so the transparent state
   * needs its own file. Absent, the normal logo is used in both states.
   */
  logoOnDark?: string;
  logoText?: string;
  logoDisplay?: "logo" | "text" | "both";
  showLogoText?: "yes" | "no";
  links: NavigationLink[];
  style?: "transparent" | "solid" | "sticky";
  /** Human-saved nav: links render exactly as stored (no implicit "/" filter). */
  manuallyManaged?: boolean;
  /**
   * Transparent style only. "auto" keeps the call cluster quiet over the hero
   * and lets it fill with the brand colour once the header solidifies; "solid"
   * keeps the brand button over photography too.
   */
  ctaStyle?: "auto" | "solid";
  /** Transparent style only: link/brand color while the header sits over the hero photo. */
  transparentText?: "light" | "dark";
  /** Bold: a dark bar in the theme's primary, the name in light type and the call button in the accent. */
  variant?: "balanced" | "compact" | "accented" | "bold";
  showPhone?: "yes" | "no";
  phone?: string;
  showTextUs?: "yes" | "no";
  showMonogram?: "yes" | "no";
  monogramText?: string;
  /**
   * Monogram colour. "brand" fills with the theme primary; "light" and "dark" are the
   * neutral pair, for logos-as-initials that shouldn't take the brand colour.
   */
  monogramStyle?: "brand" | "light" | "dark";
}

/**
 * The bold variant's colours on its dark bar. The accent marks the one thing to
 * press, the call button; everything else is the bar's light type.
 */
const BOLD_BAR = {
    brand:
      "jobsite:min-w-0 jobsite:font-heading jobsite:text-base jobsite:leading-tight jobsite:tracking-tight jobsite:text-balance jobsite:break-words jobsite:text-primary-foreground jobsite:sm:text-lg jobsite:lg:text-xl",
    call: "jobsite:bg-accent jobsite:text-accent-foreground jobsite:hover:bg-accent/90",
    link: "jobsite:text-primary-foreground/80 jobsite:hover:text-primary-foreground jobsite:hover:bg-primary-foreground/10",
    monogram: "jobsite:bg-accent jobsite:text-accent-foreground",
    outline:
      "jobsite:border-2 jobsite:border-primary-foreground/35 jobsite:text-primary-foreground jobsite:hover:bg-primary-foreground/10",
    surface: "jobsite:border-b jobsite:border-primary-foreground/10 jobsite:bg-primary",
  },
  /** The bold bar's buttons: a 44px tap target, the theme's button type. */
  BOLD_ACTION_CLASS =
    "jobsite:inline-flex jobsite:h-11 jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:gap-2 jobsite:whitespace-nowrap jobsite:px-4 jobsite:text-sm jobsite:transition-colors jobsite:outline-none jobsite:focus-visible:ring-[3px] jobsite:focus-visible:ring-accent/60";

const Navigation: React.FC<NavigationProps> = ({
  logo,
  logoOnDark,
  logoText,
  logoDisplay = "logo",
  showLogoText,
  links,
  style = "solid",
  manuallyManaged = false,
  ctaStyle = "auto",
  transparentText = "light",
  variant = "balanced",
  showPhone,
  phone,
  showTextUs = "yes",
  showMonogram = "yes",
  monogramText,
  monogramStyle = "brand",
  id,
  className,
}) => {
  const [isScrolled, setIsScrolled] = React.useState(false),
    [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false),
    [expandedDropdown, setExpandedDropdown] = React.useState<number | null>(null),
    [showTextDialog, setShowTextDialog] = React.useState(false),
    [formState, setFormState] = React.useState<"idle" | "submitting" | "success" | "error">(
      "idle",
    ),
    [formData, setFormData] = React.useState({ message: "", name: "", phone: "" });

  React.useEffect(() => {
    if (style !== "sticky" && style !== "transparent") {
      return;
    }
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 16);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [style]);

  React.useEffect(() => {
    if (!isMobileMenuOpen) {
      setExpandedDropdown(null);
    }
  }, [isMobileMenuOpen]);

  // Auto-open SMS dialog when ?text=open is in the URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("sms") && showTextUs !== "no") {
      setShowTextDialog(true);
    }
  }, [showTextUs]);

  // Defensive: tolerate legacy/bad data where `logo` was stored as an object instead of a URL.
  const safeLogoUrl =
      typeof logo === "string" && (logo.startsWith("http") || logo.startsWith("/")) ? logo : "",
    [logoLoadFailed, setLogoLoadFailed] = React.useState(false);
  React.useEffect(() => {
    setLogoLoadFailed(false);
  }, [safeLogoUrl]);
  const hasUsableLogo = Boolean(safeLogoUrl) && !logoLoadFailed,
    safeLogoOnDarkUrl =
      typeof logoOnDark === "string" && (logoOnDark.startsWith("http") || logoOnDark.startsWith("/"))
        ? logoOnDark
        : "",
    [logoOnDarkLoadFailed, setLogoOnDarkLoadFailed] = React.useState(false);
  React.useEffect(() => {
    setLogoOnDarkLoadFailed(false);
  }, [safeLogoOnDarkUrl]);
  const hasDarkSurfaceLogo = Boolean(safeLogoOnDarkUrl) && !logoOnDarkLoadFailed,

    resolvedLogoDisplay = React.useMemo(() => {
      if (logoDisplay) {
        return logoDisplay;
      }
      if (!hasUsableLogo) {
        return "text";
      }
      if (showLogoText === "yes") {
        return "both";
      }
      if (showLogoText === "no") {
        return "logo";
      }
      return "logo";
    }, [hasUsableLogo, logoDisplay, showLogoText]),

    brandName = (logoText || "").trim(),
    brandInitials = monogramText?.trim() || getBusinessInitials(brandName),
    navLinks = Array.isArray(links)
      ? (manuallyManaged
        ? links
        : links.filter((link) => link.url !== "/"))
      : [],
    formattedPhone = formatPhone(phone),
    telHref = phone ? phoneHref(phone) : undefined,
    hasPhone = Boolean(phone),
    showPhoneLink = showPhone === "yes" && hasPhone,
    showTextUsButton = showTextUs !== "no" && hasPhone,
    showLogo = hasUsableLogo && resolvedLogoDisplay !== "text",
    showBrandText = Boolean(
      brandName &&
        (resolvedLogoDisplay === "text" || resolvedLogoDisplay === "both" || !hasUsableLogo),
    ),
    surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const,
    getUrl = (link: NavigationLink) => link.url || "#",
    handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    },
    handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formEl = e.currentTarget;
      if (!formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }
      setFormState("submitting");
      try {
        const config = getWindowJobsiteConfig();
        if (config?.apiUrl && config?.siteId) {
          const params = new URLSearchParams(window.location.search),
            isPreview = params.get("preview") === "true",
            sessionId = isPreview ? null : sessionStorage.getItem("sp_session_id") || null,
            result = await submitLead({
              apiUrl: config.apiUrl,
              backupApiUrl: config.backupApiUrl,
              businessName: config.business?.name,
              payload: {
                ...formData,
                channel: "text",
                formType: "quick-quote",
                sessionId,
              },
              siteId: config.siteId,
            });
          if (result.status === "failed") {
            throw new Error("Lead submission failed");
          }
        }
        setFormState("success");
        setFormData({ message: "", name: "", phone: "" });
        window.setTimeout(() => {
          setFormState("idle");
          setShowTextDialog(false);
        }, 3000);
      } catch {
        setFormState("error");
        window.setTimeout(() => {
          setFormState("idle");
        }, 3000);
      }
    },
    isTransparent = style === "transparent" && !isScrolled && !isMobileMenuOpen,
    isBold = variant === "bold",
    // Over a hero photo a transparent header keeps its own look; the dark bar is for every other moment.
    showsBoldBar = isBold && !isTransparent,
    darkOnPhoto = transparentText === "dark",
    ctaOverPhoto = isTransparent && ctaStyle !== "solid",
    ctaScrimClass = darkOnPhoto
      ? "jobsite:bg-white/25 jobsite:backdrop-blur-sm"
      : "jobsite:bg-black/20 jobsite:backdrop-blur-sm",
    monogramSurfaceClass = isTransparent
      ? (darkOnPhoto
        ? "jobsite:bg-black/10 jobsite:text-neutral-900 jobsite:backdrop-blur"
        : "jobsite:bg-white/15 jobsite:text-white jobsite:backdrop-blur")
      : monogramStyle === "light"
        ? "jobsite:bg-muted jobsite:text-foreground"
        : monogramStyle === "dark"
          ? "jobsite:bg-foreground jobsite:text-background"
          : "jobsite:bg-primary jobsite:text-primary-foreground",
    boldMonogram = showsBoldBar && monogramStyle === "brand",
    surfaceClass = showsBoldBar ? BOLD_BAR.surface : cn(
      // Truly transparent while over the hero: no tint, no blur, no border — the hero's own
      // Top scrim carries legibility. A frosted band here read as "not fully transparent".
      isTransparent
        ? "jobsite:border-b jobsite:border-transparent jobsite:bg-transparent"
        : "jobsite:border-b jobsite:border-border",
      !isTransparent && variant === "balanced" && "jobsite:bg-background/95 jobsite:backdrop-blur-md",
      !isTransparent && variant === "compact" && "jobsite:bg-background",
      !isTransparent &&
        variant === "accented" &&
        "jobsite:bg-linear-to-r jobsite:from-background jobsite:via-background jobsite:to-primary/5",
    ),
    // The bold bar never cuts the name short: it wraps to a second line instead.
    brandTextClass = showsBoldBar ? BOLD_BAR.brand : cn(
      "jobsite:min-w-0 jobsite:font-heading jobsite:tracking-tight",
      variant === "compact"
        ? "jobsite:text-base jobsite:sm:text-lg"
        : "jobsite:text-lg jobsite:sm:text-xl",
      isTransparent
        ? (darkOnPhoto
          ? "jobsite:text-neutral-900"
          : "jobsite:text-white")
        : (variant === "accented"
          ? "jobsite:text-primary"
          : "jobsite:text-foreground"),
      // One line that shrinks against the nav links and ends in an ellipsis. The previous
      // Two-line clamp keyed off brandName.length, which is blind to how wide the chosen
      // Font actually renders, and its tight leading sheared the descenders off g/y/p.
      "jobsite:overflow-hidden jobsite:text-ellipsis jobsite:whitespace-nowrap",
    ),
    desktopLinkClass = cn(
      "jobsite:px-3 jobsite:py-2 jobsite:text-sm jobsite:font-medium jobsite:transition-colors jobsite:whitespace-nowrap jobsite:rounded-md",
      isTransparent
        ? (darkOnPhoto
          ? "jobsite:text-neutral-900/80 jobsite:hover:text-neutral-900 jobsite:hover:bg-black/10"
          : "jobsite:text-white/90 jobsite:hover:text-white jobsite:hover:bg-white/10")
        : "jobsite:text-foreground/70 jobsite:hover:text-foreground jobsite:hover:bg-muted",
      showsBoldBar && BOLD_BAR.link,
    ),
    dropdownTriggerClass = cn(
      desktopLinkClass,
      "jobsite:inline-flex jobsite:items-center jobsite:gap-1",
    ),
    mobileLinkClass = cn(
      "jobsite:flex jobsite:w-full jobsite:items-center jobsite:justify-between jobsite:px-4 jobsite:py-3 jobsite:text-left jobsite:text-base jobsite:font-medium jobsite:transition-colors",
      isTransparent
        ? (darkOnPhoto
          ? "jobsite:text-neutral-900/95 jobsite:hover:bg-black/10"
          : "jobsite:text-white/95 jobsite:hover:bg-white/10")
        : "jobsite:text-foreground jobsite:hover:bg-muted",
      showsBoldBar && "jobsite:text-primary-foreground jobsite:hover:bg-primary-foreground/10",
    ),
    /**
     * Opens the Text Us form and puts ?sms=true in the address, so the open form survives a reload.
     * @param {React.MouseEvent} event The click on a Text Us link.
     */
    openTextDialog = (event: React.MouseEvent): void => {
      event.preventDefault();
      setIsMobileMenuOpen(false);
      setShowTextDialog(true);
      window.history.replaceState(null, "", `${window.location.pathname}?sms=true`);
    },
    /**
     * The bold bar's actions at the top of the phone menu: Text us outlined and Call in the accent.
     * @returns {React.ReactNode} The actions, or null with no phone.
     */
    renderBoldMenuActions = (): React.ReactNode => {
      if (telHref === undefined || (!showTextUsButton && !showPhoneLink)) {
        return null;
      }
      return (
        <div className={cn("jobsite:grid jobsite:gap-2 jobsite:pb-3", showTextUsButton && showPhoneLink ? "jobsite:grid-cols-2" : "jobsite:grid-cols-1")}>
          {showTextUsButton && (
            <a href="?sms=true" onClick={openTextDialog} data-slot="button" className={cn(BOLD_ACTION_CLASS, BOLD_BAR.outline)} style={surfaceRadiusStyle}>
              Text us
            </a>
          )}
          {showPhoneLink && (
            <SmartLink to={telHref} data-slot="button" className={cn(BOLD_ACTION_CLASS, BOLD_BAR.call)} style={surfaceRadiusStyle}>
              <Phone className="jobsite:size-4" aria-hidden="true" />
              Call
            </SmartLink>
          )}
        </div>
      );
    },
    /**
     * The bold bar's desktop actions: Text us, outlined, and the call button,
     * which carries the number itself so nobody has to look for it.
     * @returns {React.ReactNode} The actions, or null with no phone.
     */
    renderBoldDesktopActions = (): React.ReactNode => {
      if (telHref === undefined || (!showTextUsButton && !showPhoneLink)) {
        return null;
      }
      return (
        <div className="jobsite:hidden jobsite:shrink-0 jobsite:items-center jobsite:gap-2 jobsite:lg:flex">
          {showTextUsButton && (
            <a href="?sms=true" onClick={openTextDialog} data-slot="button" className={cn(BOLD_ACTION_CLASS, BOLD_BAR.outline)} style={surfaceRadiusStyle}>
              Text us
            </a>
          )}
          {showPhoneLink && (
            <SmartLink to={telHref} data-slot="button" className={cn(BOLD_ACTION_CLASS, "jobsite:tabular-nums", BOLD_BAR.call)} style={surfaceRadiusStyle}>
              <Phone className="jobsite:size-4" aria-hidden="true" />
              Call {formattedPhone}
            </SmartLink>
          )}
        </div>
      );
    };

  return (
    <>
      <nav
        id={id}
        className={cn(
          "jobsite:z-50 jobsite:transition-all jobsite:duration-300",
          style === "sticky" && "jobsite:sticky jobsite:top-0",
          // Transparent has to leave the flow, or it reserves a strip of page above the hero
          // And the photo never reaches the top of the screen — which is the whole point of a
          // Transparent header. It stays fixed as it fades to solid on scroll.
          style === "transparent"
            ? "jobsite:fixed jobsite:inset-x-0 jobsite:top-0"
            : "jobsite:relative",
          // Bold holds the bar from tablet width up. On a phone the call bar at the
          // Bottom is the call button that stays, so the two never stack on a small screen.
          isBold && style === "sticky" && "jobsite:md:sticky jobsite:md:top-0",
          surfaceClass,
          isScrolled && "jobsite:shadow-md",
          className,
        )}
      >
        <div className={isBold ? BOLD_CONTAINER_CLASS : "jobsite:container jobsite:mx-auto jobsite:px-4"}>
          <div
            className={
              isBold
                ? "jobsite:flex jobsite:min-h-16 jobsite:items-center jobsite:justify-between jobsite:gap-3 jobsite:py-2 jobsite:lg:min-h-[4.5rem]"
                : "jobsite:flex jobsite:items-center jobsite:justify-between jobsite:gap-4 jobsite:h-16"
            }
          >
            {/* Brand */}
            <SmartLink
              to="/"
              className="jobsite:flex jobsite:min-w-0 jobsite:overflow-hidden jobsite:items-center jobsite:gap-3"
              aria-label={brandName || "Home"}
            >
              {showLogo ? (
                // Both logos are stacked and crossfaded rather than swapped by src, so the
                // Change is as smooth as the header's own colour transition and never flashes
                // An empty box while the second file loads.
                <span
                  className={cn(
                    "jobsite:relative jobsite:block jobsite:shrink-0",
                    variant === "compact" ? "jobsite:h-8" : "jobsite:h-9",
                  )}
                >
                  <img
                    crossOrigin="anonymous"
                    src={safeLogoUrl}
                    alt={logoText || "Logo"}
                    onError={() => {
                      setLogoLoadFailed(true);
                    }}
                    className={cn(
                      "jobsite:h-full jobsite:w-auto jobsite:object-contain jobsite:transition-opacity jobsite:duration-300",
                      hasDarkSurfaceLogo && isTransparent && !darkOnPhoto && "jobsite:opacity-0",
                    )}
                  />
                  {hasDarkSurfaceLogo && (
                    <img
                      crossOrigin="anonymous"
                      src={safeLogoOnDarkUrl}
                      alt=""
                      aria-hidden="true"
                      onError={() => {
                        setLogoOnDarkLoadFailed(true);
                      }}
                      className={cn(
                        "jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-auto jobsite:object-contain jobsite:transition-opacity jobsite:duration-300",
                        isTransparent && !darkOnPhoto ? "jobsite:opacity-100" : "jobsite:opacity-0",
                      )}
                    />
                  )}
                </span>
              ) : (showMonogram === "yes" && brandInitials ? (
                <span
                  className={cn(
                    "jobsite:flex jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:rounded-lg jobsite:font-heading",
                    // A square box only fits two characters. Three needs to grow sideways,
                    // So the width becomes a floor and padding does the rest.
                    variant === "compact"
                      ? "jobsite:h-8 jobsite:min-w-8 jobsite:text-sm"
                      : "jobsite:h-9 jobsite:min-w-9 jobsite:text-sm",
                    brandInitials.length > 2 ? "jobsite:px-2 jobsite:tracking-tight" : "",
                    monogramSurfaceClass,
                    boldMonogram && BOLD_BAR.monogram,
                  )}
                  aria-hidden="true"
                >
                  {brandInitials}
                </span>
              ) : null)}
              {showBrandText && (
                <span className={brandTextClass} title={brandName}>
                  {brandName}
                </span>
              )}
            </SmartLink>

            {/* Desktop nav links; bold sets them beside its buttons, not adrift in the middle of the bar */}
            <div className={cn("jobsite:hidden jobsite:shrink-0 jobsite:items-center jobsite:gap-1 jobsite:lg:flex", isBold && "jobsite:ml-auto jobsite:mr-2")}>
              {navLinks.map((link, idx) =>
                link.linkType === "dropdown" && link.children?.length ? (
                  <div key={idx} className="jobsite:relative jobsite:group">
                    <SmartLink to={getUrl(link)} className={dropdownTriggerClass}>
                      {link.label}
                      <ChevronDown className="jobsite:w-3.5 jobsite:h-3.5 jobsite:opacity-50 jobsite:transition-transform jobsite:group-hover:rotate-180" />
                    </SmartLink>
                    <div className="jobsite:absolute jobsite:left-0 jobsite:top-full jobsite:pt-2 jobsite:opacity-0 jobsite:invisible jobsite:group-hover:opacity-100 jobsite:group-hover:visible jobsite:group-focus-within:opacity-100 jobsite:group-focus-within:visible jobsite:transition-all jobsite:duration-200">
                      <div
                        className="jobsite:min-w-[200px] jobsite:border jobsite:border-border jobsite:bg-background jobsite:p-1.5 jobsite:shadow-xl"
                        style={surfaceRadiusStyle}
                      >
                        {/* Drop the child that links back to the dropdown's own
                            page — the trigger already navigates there, so it
                            reads as a redundant duplicate (e.g. "Services"
                            inside the Services menu). Mobile keeps it: its
                            trigger only expands, so that child is the only way
                            to reach the index page there. */}
                        {link.children
                          .filter((child) => getUrl(child) !== getUrl(link))
                          .map((child, childIdx) => (
                            <SmartLink
                              key={childIdx}
                              to={getUrl(child)}
                              className="jobsite:block jobsite:px-3 jobsite:py-2 jobsite:text-sm jobsite:font-medium jobsite:text-foreground jobsite:transition-colors jobsite:hover:bg-muted"
                              style={surfaceRadiusStyle}
                            >
                              {child.label}
                            </SmartLink>
                          ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <SmartLink key={idx} to={getUrl(link)} className={desktopLinkClass}>
                    {link.label}
                  </SmartLink>
                ),
              )}
            </div>

            {/* Right side: Text Us | Phone | Hamburger */}
            <div className={cn("jobsite:flex jobsite:shrink-0 jobsite:items-center", isBold ? "jobsite:gap-2" : "jobsite:gap-3")}>
              {isBold && renderBoldDesktopActions()}
              {/* Desktop: "Text Us | (555) 123-4567" as a single unit */}
              {!isBold && (showTextUsButton || showPhoneLink) && telHref && (
                <div
                  className={cn(
                    "jobsite:hidden jobsite:lg:flex jobsite:shrink-0 jobsite:items-center jobsite:whitespace-nowrap jobsite:text-sm jobsite:font-semibold jobsite:border jobsite:overflow-hidden",
                    ctaOverPhoto
                      ? cn(
                          ctaScrimClass,
                          darkOnPhoto
                            ? "jobsite:border-black/20 jobsite:text-neutral-900"
                            : "jobsite:border-white/25 jobsite:text-white",
                        )
                      : "jobsite:border-border jobsite:text-foreground",
                    // Solid over photography needs its own opaque surface, or
                    // Foreground-coloured text sits on the image unreadably.
                    isTransparent && !ctaOverPhoto && "jobsite:bg-background",
                  )}
                  style={surfaceRadiusStyle}
                >
                  {showTextUsButton && (
                    <a
                      href="?sms=true"
                      onClick={openTextDialog}
                      className={cn(
                        "jobsite:px-4 jobsite:py-2.5 jobsite:transition-colors",
                        ctaOverPhoto
                          ? (darkOnPhoto
                            ? "jobsite:hover:bg-black/10"
                            : "jobsite:hover:bg-white/10")
                          : "jobsite:bg-primary jobsite:text-primary-foreground jobsite:hover:bg-primary/90",
                      )}
                    >
                      Text Us
                    </a>
                  )}
                  {showTextUsButton && showPhoneLink && (
                    <span
                      className={cn(
                        "jobsite:w-px jobsite:self-stretch",
                        ctaOverPhoto
                          ? (darkOnPhoto
                            ? "jobsite:bg-black/20"
                            : "jobsite:bg-white/25")
                          : "jobsite:bg-border",
                      )}
                    />
                  )}
                  {showPhoneLink && (
                    <SmartLink
                      to={telHref}
                      className={cn(
                        "jobsite:px-4 jobsite:py-2.5 jobsite:transition-colors jobsite:tabular-nums",
                        ctaOverPhoto
                          ? (darkOnPhoto
                            ? "jobsite:hover:bg-black/10"
                            : "jobsite:hover:bg-white/10")
                          : "jobsite:hover:bg-muted",
                      )}
                    >
                      {formattedPhone}
                    </SmartLink>
                  )}
                </div>
              )}

              {/* Mobile: compact call button. Bold fills it with the accent, the one thing on the bar to press. */}
              {isBold && showPhoneLink && telHref && (
                <SmartLink
                  to={telHref}
                  data-slot="button"
                  className={cn(BOLD_ACTION_CLASS, BOLD_BAR.call, "jobsite:px-3.5 jobsite:lg:hidden")}
                  style={surfaceRadiusStyle}
                  aria-label={`Call ${formattedPhone}`}
                >
                  <Phone className="jobsite:size-4" aria-hidden="true" />
                  Call
                </SmartLink>
              )}
              {!isBold && showPhoneLink && telHref && (
                <SmartLink
                  to={telHref}
                  className={cn(
                    "jobsite:flex jobsite:items-center jobsite:justify-center jobsite:h-10 jobsite:px-3 jobsite:text-sm jobsite:font-semibold jobsite:border jobsite:transition-colors jobsite:lg:hidden",
                    ctaOverPhoto
                      ? cn(
                          ctaScrimClass,
                          darkOnPhoto
                            ? "jobsite:border-black/20 jobsite:text-neutral-900 jobsite:hover:bg-black/10"
                            : "jobsite:border-white/25 jobsite:text-white jobsite:hover:bg-white/10",
                        )
                      : "jobsite:border-border jobsite:text-foreground jobsite:hover:bg-muted",
                    isTransparent && !ctaOverPhoto && "jobsite:bg-background",
                  )}
                  style={surfaceRadiusStyle}
                  aria-label={`Call ${formattedPhone}`}
                >
                  Call
                </SmartLink>
              )}

              {/* Mobile hamburger */}
              <button
                type="button"
                className={cn(
                  "jobsite:flex jobsite:h-10 jobsite:w-10 jobsite:items-center jobsite:justify-center jobsite:border jobsite:transition-colors jobsite:lg:hidden",
                  isTransparent
                    ? (darkOnPhoto
                      ? "jobsite:border-black/15 jobsite:text-neutral-900 jobsite:hover:bg-black/10"
                      : "jobsite:border-white/15 jobsite:text-white jobsite:hover:bg-white/10")
                    : "jobsite:border-border jobsite:text-foreground jobsite:hover:bg-muted",
                  // A 44px tap target, light on the dark bar.
                  isBold && "jobsite:h-11 jobsite:w-11 jobsite:shrink-0",
                  showsBoldBar && "jobsite:border-2 jobsite:border-primary-foreground/35 jobsite:text-primary-foreground jobsite:hover:bg-primary-foreground/10",
                )}
                style={surfaceRadiusStyle}
                onClick={() => {
                  setIsMobileMenuOpen((open) => !open);
                }}
                aria-expanded={isMobileMenuOpen}
                aria-controls={id ? `${id}-mobile-menu` : "navigation-mobile-menu"}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMobileMenuOpen ? (
                  <X className="jobsite:w-5 jobsite:h-5" />
                ) : (
                  <Menu className="jobsite:w-5 jobsite:h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div
              id={id ? `${id}-mobile-menu` : "navigation-mobile-menu"}
              className={cn(
                "jobsite:lg:hidden jobsite:border-t jobsite:py-4",
                isTransparent
                  ? (darkOnPhoto
                    ? "jobsite:border-black/10"
                    : "jobsite:border-white/10")
                  : "jobsite:border-border",
                showsBoldBar && "jobsite:border-primary-foreground/10",
              )}
            >
              <div className="jobsite:flex jobsite:flex-col jobsite:gap-2">
                {/* Text Us + Call Now; on the bold bar, Text us outlined and Call in the accent */}
                {isBold && renderBoldMenuActions()}
                {!isBold && (showTextUsButton || showPhoneLink) && telHref && (
                  <div
                    className={cn(
                      "jobsite:grid jobsite:gap-2 jobsite:pb-3",
                      showTextUsButton && showPhoneLink
                        ? "jobsite:grid-cols-2"
                        : "jobsite:grid-cols-1",
                    )}
                  >
                    {showTextUsButton && (
                      <Button
                        asChild
                        variant="default"
                        className="jobsite:h-11 jobsite:font-semibold"
                      >
                        <a href="?sms=true" onClick={openTextDialog}>
                          Text Us
                        </a>
                      </Button>
                    )}
                    {showPhoneLink && (
                      <Button
                        asChild
                        variant="secondary"
                        className="jobsite:h-11 jobsite:font-semibold"
                      >
                        <SmartLink to={telHref}>Call Now</SmartLink>
                      </Button>
                    )}
                  </div>
                )}

                {navLinks.map((link, idx) => {
                  if (link.linkType === "dropdown" && link.children?.length) {
                    return (
                      <div
                        key={idx}
                        className={cn(
                          "jobsite:border jobsite:border-border/70 jobsite:bg-background/70 jobsite:p-1",
                          showsBoldBar && "jobsite:border-primary-foreground/15 jobsite:bg-transparent",
                        )}
                        style={surfaceRadiusStyle}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedDropdown((c) => (c === idx ? null : idx));
                          }}
                          className={mobileLinkClass}
                          style={surfaceRadiusStyle}
                          aria-expanded={expandedDropdown === idx}
                        >
                          <span>{link.label}</span>
                          <ChevronDown
                            className={cn(
                              "jobsite:w-4 jobsite:h-4 jobsite:transition-transform",
                              expandedDropdown === idx && "jobsite:rotate-180",
                            )}
                          />
                        </button>
                        {expandedDropdown === idx && (
                          <div className="jobsite:space-y-1 jobsite:px-2 jobsite:pb-2">
                            {link.children.map((child, childIdx) => (
                              <SmartLink
                                key={childIdx}
                                to={getUrl(child)}
                                className={cn(
                                  "jobsite:block jobsite:px-4 jobsite:py-2.5 jobsite:text-sm jobsite:font-medium jobsite:text-muted-foreground jobsite:transition-colors jobsite:hover:bg-muted jobsite:hover:text-foreground",
                                  showsBoldBar && "jobsite:text-primary-foreground/80 jobsite:hover:bg-primary-foreground/10 jobsite:hover:text-primary-foreground",
                                )}
                                style={surfaceRadiusStyle}
                                onClick={() => {
                                  setIsMobileMenuOpen(false);
                                }}
                              >
                                {child.label}
                              </SmartLink>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <SmartLink
                      key={idx}
                      to={getUrl(link)}
                      className={cn(
                        mobileLinkClass,
                        "jobsite:border jobsite:border-border/70 jobsite:bg-background/70",
                        showsBoldBar && "jobsite:border-primary-foreground/15 jobsite:bg-transparent",
                      )}
                      style={surfaceRadiusStyle}
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <span>{link.label}</span>
                    </SmartLink>
                  );
                },
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Text Us Dialog */}
      <Dialog open={showTextDialog} onOpenChange={setShowTextDialog}>
        <DialogContent className="jobsite:sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="jobsite:font-heading jobsite:text-2xl">
              Send Us a Text
            </DialogTitle>
            <DialogDescription>We usually respond via text within a few minutes.</DialogDescription>
          </DialogHeader>

          {formState === "success" ? (
            <div className="jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-3 jobsite:py-6 jobsite:text-center">
              <CheckCircle2 className="jobsite:h-12 jobsite:w-12 jobsite:text-emerald-500" />
              <p className="jobsite:text-lg jobsite:font-semibold jobsite:text-foreground">
                Message sent!
              </p>
              <p className="jobsite:text-sm jobsite:text-muted-foreground">
                We'll text you back shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="jobsite:space-y-4">
              <div className="jobsite:grid jobsite:gap-4 jobsite:sm:grid-cols-2">
                <Input
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="Your Name *"
                  required
                  value={formData.name}
                  onChange={handleFormChange}
                  disabled={formState === "submitting"}
                  className="jobsite:h-12 jobsite:text-base"
                />
                <Input
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="Phone Number *"
                  required
                  value={formData.phone}
                  onChange={handleFormChange}
                  disabled={formState === "submitting"}
                  className="jobsite:h-12 jobsite:text-base"
                />
              </div>
              <Textarea
                name="message"
                placeholder="How can we help? (optional)"
                rows={3}
                value={formData.message}
                onChange={handleFormChange}
                disabled={formState === "submitting"}
                className="jobsite:resize-none jobsite:text-base"
              />
              <Button
                type="submit"
                disabled={formState === "submitting"}
                className="jobsite:h-12 jobsite:w-full jobsite:font-semibold jobsite:text-base"
                size="lg"
              >
                {formState === "submitting" ? "Sending..." : "Send"}
              </Button>
              {formState === "error" && (
                <div className="jobsite:flex jobsite:items-center jobsite:gap-2 jobsite:text-sm jobsite:text-destructive">
                  <XCircle className="jobsite:h-4 jobsite:w-4 jobsite:shrink-0" />
                  <span>Something went wrong. Please try again.</span>
                </div>
              )}
              <p className="jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground">
                By providing your phone number, you consent to receive calls and text messages
                (SMS). You can unsubscribe at any time by replying STOP. Message and data rates may
                apply.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export type { NavigationLink, NavigationProps };
export { Navigation };
